import { useMemo } from 'react';

import { Grouping as H2OGPTeGrouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { FormattedUsageDataPoint, TableDataResult } from '../types';
import { formatUsageDataForTable } from '../utils/tableDataTransformer';

/**
 * Get display header text for a grouping enum value.
 * Supports both AI Units Grouping and H2OGPTe Grouping enums.
 */
function getGroupByHeader(groupBy: Grouping | H2OGPTeGrouping): string {
  switch (groupBy) {
    case Grouping.WORKSPACE:
    case H2OGPTeGrouping.WORKSPACE:
      return 'Workspace';
    case Grouping.USER:
    case H2OGPTeGrouping.USER:
      return 'User';
    case Grouping.SERVICE:
      return 'Service';
    case Grouping.TAG:
      return 'Tag';
    case H2OGPTeGrouping.MODEL:
      return 'Model';
    default:
      return 'Group';
  }
}

/**
 * Custom hook to transform chart data into table format
 *
 * Takes the same data used for the chart and pivots it so that:
 * - Rows represent groups (workspaces, users, etc.) sorted alphabetically
 * - Columns represent time periods in chronological order
 *
 * @param formattedUsageData Chart data points
 * @param groups Unique group names from chart data
 * @param groupBy Current grouping type (determines first column header) - supports both AI Units and H2OGPTe Grouping
 * @returns Table rows, time columns, and first column header text
 */
export function useTableData(
  formattedUsageData: FormattedUsageDataPoint[],
  groups: string[],
  groupBy: Grouping | H2OGPTeGrouping
): TableDataResult & { firstColumnHeader: string } {
  return useMemo(() => {
    const firstColumnHeader = getGroupByHeader(groupBy);

    if (!formattedUsageData.length || !groups.length) {
      return { rows: [], timeColumns: [], firstColumnHeader };
    }

    const { rows, timeColumns } = formatUsageDataForTable(formattedUsageData, groups);
    return { rows, timeColumns, firstColumnHeader };
  }, [formattedUsageData, groups, groupBy]);
}
